import React, { Component } from 'react';
import axios from 'axios';
import phoneFormatter from 'phone-formatter';
import Skeleton from '@material-ui/lab/Skeleton';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import MessageIcon from '@material-ui/icons/Message';
import CallIcon from '@material-ui/icons/Call';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentButtons from './PaymentButtons';
import getAmountLabel from '../../utils/getAmountLabel';
import getDateLabel from '../../utils/getDateLabel';

import {
	Address,
	Amount,
	BusinessName,
	Branding,
	Date,
	Description,
	InvoiceBody,
	InvoiceContainer,
	LogoContainer,
	Logo,
	TopSection,
	MiddleSection,
	BottomSection,
	PaymentSuccess,
	ConvenienceFee,
	TotalAmount,
} from '../styled-components';

import Receipt from './Receipt';
import dictionary from '../../utils/dictionary';

const acceptableLanguages = ['en', 'fr', 'es'];

const CHEKKIT_GREEN = '#03B856';

class Invoice extends Component {
	state = {
		loading: true,
		error: false,
		invoiceData: null,
		stripePromise: null,
		language: '',
	};

	async componentDidMount() {
		await this.setLanguage();
		this.setTitle();
		await this.getInvoiceData();
		this.markAsOpened();
	}

	setLanguage = async () => {
		const language = new URLSearchParams(this.props.location.search).get(
			'language'
		);

		await this.setState({ language });
	};

	setTitle = () => {
		const { language } = this.state;
		const { title } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		document.title = title;
	};

	getInvoiceData = async () => {
		try {
			const { language } = this.state;
			const { _invoice } = this.props.match.params;
			this.setState({ loading: true });

			const res = await axios.get(`/api/invoices/${_invoice}`);
			const { invoiceData, stripeAccountId } = res.data;

			// Make sure to call `loadStripe` outside of a component’s render to avoid
			// recreating the `Stripe` object on every render.
			const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY, {
				locale: language || 'auto',
				stripeAccount: stripeAccountId,
			});

			this.setState({
				invoiceData,
				stripePromise,
				loading: false,
			});
		} catch (err) {
			console.log(err);
			this.setState({ error: true, loading: false });
		}
	};

	formatPhone = (phone) => {
		if (phone) {
			return phoneFormatter.format(phone, '(NNN) NNN NNNN');
		}
		return '';
	};

	markAsOpened = () => {
		try {
			const { _invoice } = this.props.match.params;

			axios.post('/api/mark-invoice-as-opened', {
				_invoice,
			});

			return true;
		} catch (err) {
			console.log(err);
			return err;
		}
	};

	renderLogo = () => {
		const { invoiceData } = this.state;
		const { logoUrl } = invoiceData;

		const style = {
			backgroundColor: '#fff',
			backgroundImage: `url(${logoUrl})`,
			marginBottom: 'auto',
			marginLeft: 'auto',
			marginRight: 'auto',
			marginTop: -50,
		};

		return (
			<Logo
				header
				hasImage={style.backgroundImage}
				style={style}
				alt="logo"
			/>
		);
	};

	renderAmount = () => {
		const { invoiceData } = this.state;
		const { paymentIntent, hasConvenienceFee } = invoiceData;
		const { amount, currency } = paymentIntent;

		if (hasConvenienceFee) {
			// calculate the original amount without the convenience fee
			const originalAmount = Math.round(amount / 1.029);

			// calculate the convenience fee
			const convenienceFee = amount - originalAmount;

			return (
				<div style={{ marginBottom: 10 }}>
					<Amount>
						{getAmountLabel({
							amount,
							currency,
						})}
					</Amount>
					<TotalAmount>
						Amount:{' '}
						{getAmountLabel({ amount: originalAmount, currency })}
					</TotalAmount>
					<ConvenienceFee>
						Convenience Fee:{' '}
						{getAmountLabel({
							amount: convenienceFee,
							currency,
						})}
					</ConvenienceFee>
				</div>
			);
		}

		return <Amount>{getAmountLabel({ amount, currency })}</Amount>;
	};

	renderLoading = () => (
		<InvoiceBody className="center">
			<InvoiceContainer>
				<LogoContainer>
					<Skeleton
						animation="wave"
						variant="circle"
						width={100}
						height={100}
					/>
				</LogoContainer>
				<TopSection>
					<BusinessName>
						<Skeleton animation="wave" height={27} width="80%" />
					</BusinessName>

					<Address>
						<Skeleton animation="wave" height={22} width="80%" />
					</Address>
				</TopSection>
				<Divider />
				<MiddleSection>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<Skeleton
							animation="wave"
							height={21}
							width="80%"
							justify="center"
						/>
					</div>

					<Description>
						<Skeleton animation="wave" height={21} width="80%" />
					</Description>

					<Amount>
						<Skeleton animation="wave" height={47} width="80%" />
					</Amount>
					<Date>
						<Skeleton animation="wave" height={24} width="80%" />
					</Date>
				</MiddleSection>
				<Divider />
				<BottomSection>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<Skeleton animation="wave" height={90} width="80%" />
					</div>
				</BottomSection>
			</InvoiceContainer>
			<Branding>
				<Skeleton
					animation="wave"
					height={19}
					width="30%"
					style={{ visibility: 'hidden' }}
				/>
			</Branding>
		</InvoiceBody>
	);

	renderError = () => {
		const { language } = this.state;
		const { errorTitle, errorMessage } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		return (
			<Grid
				container
				direction="column"
				justify="center"
				alignItems="center"
				style={{ height: '60%' }}
			>
				<Grid item>
					<h3>{errorTitle}</h3>
				</Grid>

				<Grid item>
					<div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
						{errorMessage}
					</div>
				</Grid>
			</Grid>
		);
	};

	renderCheckoutForm = () => {
		const { invoiceData, language } = this.state;
		const { _invoice } = this.props.match.params;
		const {
			paymentSuccessTitle,
			paymentSuccessBody,
			voidMessage,
			text,
			call,
		} =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		if (!invoiceData) {
			return <div />;
		}

		const {
			brandColor,
			paymentIntent,
			country,
			name: businessName,
			twilioNumber,
			hasConvenienceFee,
		} = invoiceData;

		const { amount } = paymentIntent;

		// calculate the original amount without the convenience fee
		const originalAmount = Math.round(amount / 1.029);

		// calculate the convenience fee
		const convenienceFee = amount - originalAmount;

		console.log({ convenienceFee });

		const { client_secret: clientSecret, currency, status } = paymentIntent;

		if (paymentIntent && status === 'succeeded') {
			return (
				<div>
					<div style={{ marginBottom: '15px' }}>
						<h5
							style={{
								color: CHEKKIT_GREEN,
								marginBottom: '15px',
							}}
						>
							{paymentSuccessTitle}{' '}
						</h5>
						<PaymentSuccess>{paymentSuccessBody}</PaymentSuccess>
					</div>
					{hasConvenienceFee ? (
						<Receipt
							invoiceData={invoiceData}
							convenienceFee={convenienceFee}
							originalAmount={originalAmount}
							hasConvenienceFee={hasConvenienceFee}
							language={language}
							_invoice={_invoice}
							currency={currency}
						/>
					) : (
						<Receipt
							invoiceData={invoiceData}
							hasConvenienceFee={hasConvenienceFee}
							language={language}
							_invoice={_invoice}
							currency={currency}
						/>
					)}
				</div>
			);
		}

		if (status === 'canceled') {
			return (
				<div>
					<div style={{ marginBottom: '15px' }}>{voidMessage}</div>
					<Button variant="contained" href={`sms:${twilioNumber}`}>
						<MessageIcon style={{ paddingRight: '5px' }} />
						{text}
					</Button>{' '}
					<Button variant="contained" href={`tel:${twilioNumber}`}>
						<CallIcon style={{ paddingRight: '5px' }} />
						{call}
					</Button>
				</div>
			);
		}

		return (
			<PaymentButtons
				amount={amount}
				businessName={businessName}
				country={country}
				currency={currency}
				brandColor={brandColor}
				clientSecret={clientSecret}
				language={language}
				_invoice={_invoice}
				invoiceData={invoiceData}
			/>
		);
	};

	renderInvoice = () => {
		const { language, invoiceData } = this.state;

		const { billed, forText, requested } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		const {
			name,
			address,
			paymentIntent,
			customerName,
			customerPhone,
			customerEmail,
		} = invoiceData;

		// the name to show for the customer (can be name phone or email )
		let customerNameToShow;

		if (customerName && customerName !== '') {
			customerNameToShow = customerName;
		} else if (customerPhone) {
			customerNameToShow = this.formatPhone(customerPhone);
		} else if (customerEmail) {
			customerNameToShow = customerEmail;
		}

		const { created, description } = paymentIntent;

		return (
			<InvoiceContainer>
				<TopSection>
					{this.renderLogo()}
					<BusinessName>{name}</BusinessName>
					<Address>{address}</Address>
				</TopSection>
				<Divider />
				<MiddleSection>
					{this.renderAmount()}

					<Date>{`${requested} ${getDateLabel(created)}`}</Date>
					<Description>{`${billed} ${customerNameToShow} ${forText} ${description}`}</Description>
				</MiddleSection>
				<Divider />
				<BottomSection>{this.renderCheckoutForm()}</BottomSection>
			</InvoiceContainer>
		);
	};

	render() {
		const { error, stripePromise, loading, language } = this.state;
		const { branding } =
			dictionary[
				acceptableLanguages.includes(language) ? language : 'en'
			];

		if (error) {
			return this.renderError();
		}

		if (loading) {
			return this.renderLoading();
		}

		return (
			<Elements stripe={stripePromise}>
				<InvoiceBody className="center">
					{this.renderInvoice()}
					<Branding href="https://www.chekkit.io" target="_blank">
						{branding} Chekkit
					</Branding>
				</InvoiceBody>
			</Elements>
		);
	}
}

export default Invoice;



// WEBPACK FOOTER //
// ./src/components/payments/Invoice.js